/**
 * ToolGroup — rendu d'une salve de tool calls consécutifs (design 2026-06-11,
 * docs/features/agent-case-discovery-v3/plans/2026-06-11-tool-group-summary-ui.html) :
 * une phrase résumé « Utilisé X outils, a exécuté Y sous-agents › », collapsed
 * par défaut, expandable en timeline verticale chronologique (un tool = une
 * ligne cliquable → RichDetails ; les invoke_subagent consécutifs = un nœud
 * grille de cards). Nœud final « ✓ Terminé » quand la salve est close.
 *
 * Mirror component: addin-word hector-agent-edition-v3 ToolGroup.tsx — keep
 * behavior in sync (couleurs slate + grille resserrée côté add-in).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, CheckCircle2, ChevronDown, ChevronRight, Loader2, Wrench, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TextShimmer } from './TextShimmer';
import { RichDetails } from './RichDetails';
import { useToolLineCase } from './CitationRegistryContext';
import { BACKEND_TOOL_ICONS } from '../../constants/toolLabels';
import {
  resolveBackendToolLabel,
  getCaseSubtitle,
  getToolLineSubtitle,
  getSummaryBadge,
  hasRichDetails,
  objectiveChecklist,
  latestObjectiveEvents,
  formatToolGroupPhrase,
  formatRunningToolsLabel,
  isToolRunning as isRunning,
} from './toolPresentation';
import {
  splitToolGroupIntoNodes,
  getSubagentPreviewItems,
  getCollapsedSubagentPreview,
} from '../../utils/groupTimelineItems';
import type { ToolTimelineItem, ToolGroupMember, ThinkingTimelineItem } from '../../utils/groupTimelineItems';

interface ToolGroupProps {
  /**
   * Les membres de la salve dans l'ordre du LLM : les tool calls + les blocs de
   * réflexion émis juste après un tool (réflexion sur les résultats, liée à eux).
   */
  members: ToolGroupMember[];
  /**
   * La salve est close : un item la suit dans la timeline, ou l'échange est
   * terminé (phase history / live_done). Pilote le shimmer de la phrase et le
   * nœud final « Terminé ».
   */
  isClosed: boolean;
}

const ToolGroupImpl: React.FC<ToolGroupProps> = ({ members, isClosed }) => {
  // Collapsed par défaut, toggle uniquement manuel — même décision que le
  // reasoning (c1cda9854) : aucun useEffect d'auto-expand/collapse, donc le
  // passage live → clos ne replie jamais une vue que l'user a ouverte.
  const [isExpanded, setIsExpanded] = useState(false);

  const nodes = useMemo(() => splitToolGroupIntoNodes(members), [members]);
  const subagentPreviewItems = useMemo(() => getSubagentPreviewItems(members), [members]);
  const objectifs = useMemo(
    () => latestObjectiveEvents(members.flatMap((member) => (member.type === 'tool' ? [member.event] : []))),
    [members],
  );

  // La phrase résumé ne compte que les tools (la réflexion liée est un détail
  // visible à l'ouverture, pas un « outil utilisé »).
  const toolItems = members.filter((m): m is ToolTimelineItem => m.type === 'tool');
  const subagentCount = toolItems.filter(t => t.event.tool === 'invoke_subagent').length;
  const toolCount = toolItems.length - subagentCount;
  // Salve ouverte (!isClosed) → wording présent « En cours d'utilisation/d'exécution : … » ;
  // salve close → passé « Utilisé … / a exécuté … ».
  const phrase = formatToolGroupPhrase(toolCount, subagentCount, !isClosed);
  const hasError = toolItems.some(t => t.event.status === 'error');

  // Suffixe live : les outils encore en cours, signal de progression sans
  // ouvrir le groupe. Plusieurs peuvent tourner de front dans une réponse.
  const runningLabel = !isClosed ? formatRunningToolsLabel(toolItems.map(t => t.event)) : null;

  if (members.length === 0) return null;

  return (
    // pt-1 (et non py-1) : la boîte colle à sa dernière ligne (résumé replié, ou
    // dernier nœud `last:pb-0` déplié). La distance jusqu'au prochain frère — et
    // surtout jusqu'à la plume — reste alors identique à celle après un bloc de
    // texte (flush), au lieu d'ajouter un 4px de bas variable selon le type d'item.
    <div className="pt-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-[13px] text-grey-600 hover:text-grey-600 transition-colors max-w-full"
      >
        {!isClosed ? (
          <TextShimmer className="font-normal whitespace-nowrap">{phrase}</TextShimmer>
        ) : (
          <span className="font-normal whitespace-nowrap">{phrase}</span>
        )}
        {runningLabel && (
          <span className="text-grey-400 truncate min-w-0" title={runningLabel}>· {runningLabel}…</span>
        )}
        {hasError && (
          <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" aria-label="Un outil a rencontré une erreur" />
        )}
        <ChevronRight
          className={cn(
            'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200',
            isExpanded && 'rotate-90',
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {!isExpanded && subagentPreviewItems.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="ml-1 mt-2">
              <TimelineNode showConnector={false}>
                <SubagentRun items={subagentPreviewItems} variant="activePreview" />
              </TimelineNode>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Salve repliée : l'objectif reste lisible, dans son dernier état. Dépliée, chaque ligne d'objectif porte le sien. */}
      {!isExpanded && objectifs.map((objectif) => (
        <div key={objectif.data?.id ?? objectif.tool} className="mt-2 rounded-md border border-grey-100 bg-grey-50/50">
          <RichDetails event={objectif} />
        </div>
      ))}

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="ml-1 mt-2">
              {nodes.map((node, i) => {
                // Le trait vertical relie chaque icône à la suivante ; le
                // dernier nœud (ou « Terminé ») n'a pas de trait sortant.
                const isLastNode = i === nodes.length - 1 && !isClosed;
                if (node.kind === 'thinking') {
                  return (
                    <TimelineNode key={node.item.id} showConnector={!isLastNode}>
                      <ReasoningLine item={node.item} />
                    </TimelineNode>
                  );
                }
                return node.kind === 'tool' ? (
                  <TimelineNode key={node.item.id} showConnector={!isLastNode}>
                    <ToolLine item={node.item} />
                  </TimelineNode>
                ) : (
                  <TimelineNode key={node.id} showConnector={!isLastNode}>
                    <SubagentRun items={node.items} />
                  </TimelineNode>
                );
              })}
              {isClosed && (
                <TimelineNode showConnector={false}>
                  <div className="relative z-[1] mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-grey-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[13px] font-normal text-grey-600">Terminé</span>
                  </div>
                </TimelineNode>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Mémoïsé avec comparateur custom : groupTimelineItems recrée les tableaux
 * `members` à chaque delta streamé (nouvelle référence), mais les items qu'ils
 * contiennent gardent leur référence tant que le reducer ne les mute pas
 * (immutabilité ciblée de StreamingSessionReducer). Comparer item par item
 * évite de re-render tous les groupes à chaque token de contenu.
 */
export const ToolGroup = React.memo(
  ToolGroupImpl,
  (prev, next) =>
    prev.isClosed === next.isClosed &&
    prev.members.length === next.members.length &&
    prev.members.every((m, i) => m === next.members[i]),
);

// ─── Timeline node (icône + trait vertical + contenu) ───────────────────────

export function TimelineNode({
  children,
  showConnector,
}: {
  children: React.ReactNode;
  showConnector: boolean;
}) {
  // Les enfants (ToolLine, SubagentRun, nœud Terminé) rendent chacun leur
  // colonne icône (h/w-3.5) puis leur contenu flex-1 — le trait vertical part
  // sous l'icône (top-5) et rejoint le nœud suivant.
  return (
    <div className="relative flex gap-2.5 pb-5 last:pb-0">
      {showConnector && (
        <div className="absolute left-[7px] top-5 bottom-0 w-px bg-grey-200" />
      )}
      {children}
    </div>
  );
}

// ─── Ligne tool (une phrase, cliquable → RichDetails) ───────────────────────

function ToolLine({ item }: { item: ToolTimelineItem }) {
  const event = item.event;
  // La liste à cocher d'un objectif se lit sans clic : c'est ce que l'agent s'engage à faire, puis déclare avoir fait.
  const [isOpen, setIsOpen] = useState(() => objectiveChecklist(event) !== null);
  const label = resolveBackendToolLabel(event);
  // Fallback neutre (Wrench) et non Loader2 : l'icône est rendue statiquement
  // (sans animate-spin) pour TOUS les statuts — un Loader2 figé à côté du nœud
  // « ✓ Terminé » du groupe se lisait comme « spinner + coche » sur les tools non
  // mappés (issue #223). Le statut « en cours » est déjà porté par le shimmer du
  // label, pas par l'icône.
  const IconComponent = BACKEND_TOOL_ICONS[event.tool] || Wrench;
  const { shownCaseId, caseTitleOf } = useToolLineCase();
  const subtitle = getToolLineSubtitle(event, shownCaseId, caseTitleOf);
  const badge = getSummaryBadge(event);
  const hasDetail = hasRichDetails(event);
  const inProgress = isRunning(event.status);
  const isError = event.status === 'error';

  return (
    <>
      <div className="relative z-[1] mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
        {isError ? (
          <XCircle className="w-3.5 h-3.5 text-red-400" />
        ) : (
          <IconComponent className="w-3.5 h-3.5 text-grey-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            'flex items-center gap-1.5 text-[13px]',
            hasDetail && 'cursor-pointer',
          )}
          onClick={hasDetail ? () => setIsOpen(o => !o) : undefined}
        >
          {inProgress ? (
            <TextShimmer className="font-normal text-grey-600 whitespace-nowrap flex-shrink-0">{label}</TextShimmer>
          ) : (
            <span className={cn('font-normal whitespace-nowrap flex-shrink-0', isError ? 'text-red-600' : 'text-grey-600')}>
              {label}
            </span>
          )}
          {subtitle && (
            <span className="text-grey-400 truncate min-w-0" title={subtitle}>
              · {subtitle}
            </span>
          )}
          {badge && (
            <span className="px-1.5 py-px bg-grey-100 text-grey-600 rounded text-[13px] font-normal flex-shrink-0">
              {badge}
            </span>
          )}
          {hasDetail && (
            <ChevronDown
              className={cn(
                'w-3 h-3 text-grey-400 flex-shrink-0 transition-transform duration-200',
                isOpen && 'rotate-180',
              )}
            />
          )}
        </div>
        <AnimatePresence initial={false}>
          {isOpen && hasDetail && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              {/* Même fond gris que l'ancienne carte : RichDetails a été
                  designé sur bg-grey-50, on le conserve pour la lisibilité. */}
              <div className="mt-1.5 rounded-md border border-grey-100 bg-grey-50/50">
                <RichDetails event={event} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

// ─── Ligne réflexion (cerveau + texte ; pas de titre, cropé + « Voir plus ») ──

/**
 * Une réflexion rendue comme un nœud de timeline : le cerveau (l'icône distingue
 * la réflexion d'un tool — pas de titre texte), puis la réflexion en entier,
 * cropée à 3 lignes avec un bouton « Voir plus » UNIQUEMENT si elle déborde.
 * Comportement identique partout (timeline d'un ToolGroup ET liste du haut).
 */
export function ReasoningLine({ item }: { item: ThinkingTimelineItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const { content } = item.thinking;

  // Le bouton n'apparaît que si le texte clampé déborde (scrollHeight > hauteur
  // visible). Recalculé quand le contenu grandit (streaming) ou au toggle.
  useEffect(() => {
    const el = textRef.current;
    if (!el || isOpen) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [content, isOpen]);

  if (!content) return null;

  return (
    <>
      <div className="relative z-[1] mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
        <Brain className="w-3.5 h-3.5 text-grey-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          ref={textRef}
          className={cn(
            'text-[13px] text-grey-600 whitespace-pre-wrap leading-relaxed',
            !isOpen && 'line-clamp-3',
          )}
        >
          {content}
        </p>
        {(overflowing || isOpen) && (
          <button
            type="button"
            onClick={() => setIsOpen(o => !o)}
            className="mt-0.5 flex items-center gap-0.5 text-[13px] font-normal text-grey-400 hover:text-grey-600 transition-colors"
          >
            {isOpen ? 'Voir moins' : 'Voir plus'}
            <ChevronDown
              className={cn('w-3 h-3 transition-transform duration-200', isOpen && 'rotate-180')}
            />
          </button>
        )}
      </div>
    </>
  );
}

// ─── Nœud sous-agents (grille de cards, click → détails) ────────────────────

function SubagentRun({
  items,
  variant = 'timeline',
}: {
  items: ToolTimelineItem[];
  variant?: 'timeline' | 'activePreview';
}) {
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const BotIcon = BACKEND_TOOL_ICONS.invoke_subagent || Loader2;
  const anyRunning = items.some(t => isRunning(t.event.status));
  const count = items.length;
  const label = variant === 'activePreview' && anyRunning
    ? `Exécute ${count} sous-agent${count > 1 ? 's' : ''}`
    : `A exécuté ${count} sous-agent${count > 1 ? 's' : ''}`;

  // Si ≥2 sous-agents du même type tournent dans la salve, le label seul ne
  // les distingue pas → on affiche un extrait de la consigne en sous-titre.
  const labelCounts = new Map<string, number>();
  for (const t of items) {
    const l = resolveBackendToolLabel(t.event);
    labelCounts.set(l, (labelCounts.get(l) ?? 0) + 1);
  }

  const openItem = openCardId ? items.find(t => t.id === openCardId) : undefined;
  const collapsedPreview = variant === 'activePreview'
    ? getCollapsedSubagentPreview(items)
    : { visible: items, hiddenCount: 0, hiddenStatusSummary: null };

  return (
    <>
      <div className="relative z-[1] mt-0.5 flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
        <BotIcon className="w-3.5 h-3.5 text-grey-400" />
      </div>
      <div className="flex-1 min-w-0">
        {anyRunning ? (
          <TextShimmer className="text-[13px] font-normal text-grey-600">{label}</TextShimmer>
        ) : (
          <span className="text-[13px] font-normal text-grey-600">{label}</span>
        )}
        <div
          className="mt-1.5 grid gap-1.5"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))' }}
        >
          {collapsedPreview.visible.map(item => (
            <SubagentCard
              key={item.id}
              item={item}
              showPromptExcerpt={(labelCounts.get(resolveBackendToolLabel(item.event)) ?? 0) > 1}
              isOpen={openCardId === item.id}
              onToggle={() => setOpenCardId(id => (id === item.id ? null : item.id))}
            />
          ))}
        </div>
        {collapsedPreview.hiddenCount > 0 && (
          <div className="mt-1.5 rounded-md border border-grey-100 bg-grey-50 px-2 py-1.5 text-[13px] text-grey-600">
            +{collapsedPreview.hiddenCount} autre{collapsedPreview.hiddenCount > 1 ? 's' : ''} sous-agent{collapsedPreview.hiddenCount > 1 ? 's' : ''}
            {collapsedPreview.hiddenStatusSummary && (
              <span className="text-grey-400"> · {collapsedPreview.hiddenStatusSummary}</span>
            )}
          </div>
        )}
        <AnimatePresence initial={false}>
          {openItem && hasRichDetails(openItem.event) && (
            <motion.div
              key={openItem.id}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="mt-1.5 rounded-md border border-grey-100 bg-grey-50/50">
                <RichDetails event={openItem.event} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

function SubagentCard({
  item,
  showPromptExcerpt,
  isOpen,
  onToggle,
}: {
  item: ToolTimelineItem;
  showPromptExcerpt: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const event = item.event;
  const label = resolveBackendToolLabel(event);
  const inProgress = isRunning(event.status);
  const isError = event.status === 'error';
  const prompt = typeof event.data?.prompt === 'string' ? event.data.prompt : null;
  const hasDetail = hasRichDetails(event);
  // Un sous-agent fixé sur un connexe le dit sur sa carte, comme une ligne d'outil.
  const { shownCaseId, caseTitleOf } = useToolLineCase();
  const caseMention = getCaseSubtitle(event, shownCaseId, caseTitleOf);

  return (
    <button
      type="button"
      onClick={hasDetail ? onToggle : undefined}
      className={cn(
        'rounded-md border bg-white p-2 text-left transition-colors',
        isError ? 'border-red-200' : isOpen ? 'border-grey-300' : 'border-grey-100',
        hasDetail ? 'cursor-pointer hover:border-grey-200' : 'cursor-default',
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {inProgress ? (
          <Loader2 className="w-3 h-3 text-grey-400 animate-spin flex-shrink-0" />
        ) : isError ? (
          <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-3 h-3 text-grey-400 flex-shrink-0" />
        )}
        <span className="text-[13px] font-normal text-grey-600 truncate" title={label}>
          {label}
        </span>
      </div>
      {caseMention && (
        <div className="mt-0.5 text-[13px] text-grey-400 truncate" title={caseMention}>
          {caseMention}
        </div>
      )}
      {showPromptExcerpt && prompt && (
        <div className="mt-0.5 text-[13px] text-grey-400 truncate" title={prompt}>
          {prompt}
        </div>
      )}
      <div className="mt-1">
        {inProgress ? (
          <TextShimmer className="text-[13px] font-normal px-1.5 py-px">En cours</TextShimmer>
        ) : (
          <span
            className={cn(
              'inline-block px-1.5 py-px rounded text-[13px] font-normal',
              isError ? 'bg-red-50 text-red-600' : 'bg-grey-100 text-grey-600',
            )}
          >
            {isError ? 'Erreur' : 'Terminé'}
          </span>
        )}
      </div>
    </button>
  );
}
